import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import StatisticsCards from '../../components/dashboard/stats-cards/StatisticsCards.jsx';
import { attachDashboardDragAutoScroll } from '../../utils/dashboardDragAutoScroll';
import { DASHBOARD_DRAG_APPEND_TARGET_ID } from '../../utils/dashboardDragReorder';
import { HOME_DASHBOARD_WIDE_LAYOUT_MIN_PX, normalizeHomeDashboardLayout } from '../../utils/dashboard/homeDashboardLayout';
import { resolveMasonryRowSpan } from '../../utils/dashboard/homeDashboardMasonry';
import { canAccessHomeDashboardSection } from '../../utils/dashboard/homeDashboardSectionAccess';
import DashboardLayoutSection from './DashboardLayoutSection';
import DashboardLayoutToolbar from './DashboardLayoutToolbar';
import createDashboardLayoutSectionDragGhost from './lib/createDashboardLayoutSectionDragGhost';
import HomeDashboardChartsSection from './HomeDashboardChartsSection.jsx';
import ProcessingOrderCard from './processing-order/ProcessingOrderCard.jsx';
import Overdue from './overdue-statistic/Overdue.jsx';

const EMPTY_DRAG_IMAGE =
	'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const renderSectionContent = (sectionId, { layoutEditing = false } = {}) => {
	switch (sectionId) {
		case 'statCards':
			return <StatisticsCards />;
		case 'homeCharts':
			return <HomeDashboardChartsSection layoutEditing={layoutEditing} />;
		case 'processingOrders':
			return <ProcessingOrderCard />;
		case 'overdue':
			return <Overdue />;
		default:
			return null;
	}
};

const DashboardLayoutGrid = ({
	layout,
	userInfo,
	isEditing = false,
	isResetting = false,
	actionMessage = '',
	onFinishEditing,
	onReorder,
	onToggleHidden,
	onRestoreDefault,
}) => {
	const normalized = useMemo(() => normalizeHomeDashboardLayout(layout, userInfo), [layout, userInfo]);
	const sections = useMemo(() => {
		const permitted = normalized.sections.filter((section) => canAccessHomeDashboardSection(userInfo, section.id));
		if (isEditing) return permitted;
		return permitted.filter((section) => !section.hidden);
	}, [isEditing, normalized.sections, userInfo]);

	const canReorder = isEditing && sections.length > 1;
	const sectionsRef = useRef(null);
	const dragGhostRef = useRef(null);
	const draggingIdRef = useRef(null);
	const dragOverIdRef = useRef(null);
	const [draggingId, setDraggingId] = useState(null);
	const [dragOverId, setDragOverId] = useState(null);

	const setDragOverTarget = useCallback((nextId) => {
		dragOverIdRef.current = nextId;
		setDragOverId(nextId);
	}, []);

	const clearDragState = useCallback(() => {
		if (dragGhostRef.current) {
			dragGhostRef.current.remove();
			dragGhostRef.current = null;
		}
		draggingIdRef.current = null;
		dragOverIdRef.current = null;
		setDraggingId(null);
		setDragOverId(null);
	}, []);

	useEffect(() => {
		if (!draggingId) return undefined;
		document.body.classList.add('dashboard-layout--dragging');

		const detachAutoScroll = attachDashboardDragAutoScroll({
			getRootEl: () => sectionsRef.current,
			isDraggingRef: draggingIdRef,
		});

		const onDocumentDragEnd = () => clearDragState();
		document.addEventListener('dragend', onDocumentDragEnd);

		return () => {
			document.body.classList.remove('dashboard-layout--dragging');
			detachAutoScroll();
			document.removeEventListener('dragend', onDocumentDragEnd);
		};
	}, [clearDragState, draggingId]);

	useEffect(() => {
		const root = sectionsRef.current;
		if (!root || typeof ResizeObserver === 'undefined') return undefined;

		const media = window.matchMedia(`(min-width: ${HOME_DASHBOARD_WIDE_LAYOUT_MIN_PX}px)`);
		const itemSelector = ':scope > .dashboard-layout-section, :scope > .dashboard-drag-append-zone';
		let frame = 0;

		const applyMasonrySpans = () => {
			const nodes = root.querySelectorAll(itemSelector);
			if (!media.matches) {
				nodes.forEach((el) => {
					el.style.gridRowEnd = '';
				});
				return;
			}

			const rowHeight = parseFloat(getComputedStyle(root).gridAutoRows) || 1;
			const freezingSections = Boolean(draggingIdRef.current);
			nodes.forEach((el) => {
				// 拖拽中冻结既有区块跨度，只给底部追加区补 span，避免瀑布流重排跳动。
				if (freezingSections && el.classList.contains('dashboard-layout-section') && el.style.gridRowEnd) {
					return;
				}
				const extraGap = parseFloat(getComputedStyle(el).marginBottom) || 0;
				const span = resolveMasonryRowSpan(el.getBoundingClientRect().height, rowHeight, extraGap);
				const next = `span ${span}`;
				if (el.style.gridRowEnd !== next) el.style.gridRowEnd = next;
			});
		};

		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(applyMasonrySpans);
		};

		const resizeObserver = new ResizeObserver(schedule);
		const observeItems = () => {
			resizeObserver.disconnect();
			resizeObserver.observe(root);
			root.querySelectorAll(itemSelector).forEach((el) => resizeObserver.observe(el));
		};

		const mutationObserver = new MutationObserver(() => {
			observeItems();
			schedule();
		});

		observeItems();
		mutationObserver.observe(root, { childList: true });
		media.addEventListener('change', schedule);
		schedule();

		return () => {
			cancelAnimationFrame(frame);
			resizeObserver.disconnect();
			mutationObserver.disconnect();
			media.removeEventListener('change', schedule);
			root.querySelectorAll(itemSelector).forEach((el) => {
				el.style.gridRowEnd = '';
			});
		};
	}, [sections, isEditing]);

	const handleDragStart = useCallback(
		(event, sectionId) => {
			if (!canReorder) return;
			event.stopPropagation();
			draggingIdRef.current = sectionId;
			event.dataTransfer.effectAllowed = 'move';
			event.dataTransfer.setData('text/plain', sectionId);

			const handleEl = event.currentTarget;
			if (handleEl instanceof Element) {
				const { host, offsetX, offsetY } = createDashboardLayoutSectionDragGhost(handleEl, event, sectionId);
				dragGhostRef.current = host;
				event.dataTransfer.setDragImage(host, offsetX, offsetY);
			} else {
				const emptyImage = new Image();
				emptyImage.src = EMPTY_DRAG_IMAGE;
				event.dataTransfer.setDragImage(emptyImage, 0, 0);
			}

			setDraggingId(sectionId);
			setDragOverTarget(null);
		},
		[canReorder, setDragOverTarget],
	);

	const handleDragEnd = useCallback(() => {
		clearDragState();
	}, [clearDragState]);

	const handleDragOver = useCallback(
		(event, sectionId) => {
			const sourceId = draggingIdRef.current;
			if (!sourceId || sectionId === sourceId) return;
			event.preventDefault();
			event.stopPropagation();
			event.dataTransfer.dropEffect = 'move';
			// 高亮悬停的整块；松手与之交换（不再按上下半区「插到前面」）
			setDragOverTarget(sectionId);
		},
		[setDragOverTarget],
	);

	const handleAppendDragOver = useCallback(
		(event) => {
			const sourceId = draggingIdRef.current;
			if (!sourceId) return;
			event.preventDefault();
			event.stopPropagation();
			event.dataTransfer.dropEffect = 'move';
			setDragOverTarget(DASHBOARD_DRAG_APPEND_TARGET_ID);
		},
		[setDragOverTarget],
	);

	/** 松手前可能丢 sticky；回退到指针下的区块 id。 */
	const resolveDropTargetId = useCallback((clientX, clientY) => {
		const sticky = dragOverIdRef.current;
		if (sticky) return sticky;

		const root = sectionsRef.current;
		if (!root || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;

		const hit = document.elementFromPoint(clientX, clientY);
		if (!(hit instanceof Element) || !root.contains(hit)) return null;
		if (hit.closest('.dashboard-drag-append-zone')) return DASHBOARD_DRAG_APPEND_TARGET_ID;

		const sectionEl = hit.closest('.dashboard-layout-section');
		const hoveredId = sectionEl?.getAttribute('data-dashboard-section-id');
		const sourceId = draggingIdRef.current;
		if (!hoveredId || !sourceId || hoveredId === sourceId) return null;
		return hoveredId;
	}, []);

	const commitDrop = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();
			const sourceId = event.dataTransfer.getData('text/plain') || draggingIdRef.current;
			const targetId = resolveDropTargetId(event.clientX, event.clientY);
			if (sourceId && targetId && (targetId === DASHBOARD_DRAG_APPEND_TARGET_ID || sourceId !== targetId)) {
				onReorder?.(sourceId, targetId);
			}
			clearDragState();
		},
		[clearDragState, onReorder, resolveDropTargetId],
	);

	const handleSectionsDragOver = useCallback(
		(event) => {
			if (!draggingIdRef.current) return;
			event.preventDefault();
			event.dataTransfer.dropEffect = 'move';
		},
		[],
	);

	return (
		<div
			className={[
				'dashboard-layout-grid',
				isEditing ? 'dashboard-layout-grid--editing' : null,
				draggingId ? 'dashboard-layout-grid--reordering' : null,
			]
				.filter(Boolean)
				.join(' ')}
		>
			{isEditing ? (
				<DashboardLayoutToolbar isResetting={isResetting} onFinishEditing={onFinishEditing} onRestoreDefault={onRestoreDefault} />
			) : null}
			{actionMessage ? <p className='dashboard-layout-grid__message text-muted small mb-2'>{actionMessage}</p> : null}
			{!isEditing && sections.length === 0 ? (
				<div className='dashboard-layout-grid__empty card border-0 text-center py-4 px-3'>
					<p className='text-muted small mb-3'>所有首页区块均已隐藏</p>
					<Button type='button' size='sm' variant='outline-primary' onClick={onRestoreDefault} disabled={isResetting}>
						恢复默认布局
					</Button>
				</div>
			) : (
				<div
					ref={sectionsRef}
					className='dashboard-layout-grid__sections'
					onDragOver={handleSectionsDragOver}
					onDrop={commitDrop}
				>
					{sections.map((section) => {
							const sectionContent = renderSectionContent(section.id, { layoutEditing: isEditing });
							if (!isEditing && (sectionContent == null || !canAccessHomeDashboardSection(userInfo, section.id))) {
								return null;
							}

						const isDropTarget = Boolean(draggingId && dragOverId === section.id && draggingId !== section.id);
						return (
							<DashboardLayoutSection
								key={section.id}
								section={section}
								isEditing={isEditing}
								isDragging={draggingId === section.id}
								isDragTarget={isDropTarget}
								canReorder={canReorder}
								onDragHandleStart={(event) => handleDragStart(event, section.id)}
								onDragHandleEnd={handleDragEnd}
								onToggleHidden={onToggleHidden}
								onDragEnter={(event) => event.preventDefault()}
								onDragOver={(event) => handleDragOver(event, section.id)}
								onDrop={commitDrop}
							>
								{sectionContent}
							</DashboardLayoutSection>
						);
					})}
					{draggingId ? (
						<div
							className={[
								'dashboard-drag-append-zone',
								dragOverId === DASHBOARD_DRAG_APPEND_TARGET_ID ? 'dashboard-drag-append-zone--active' : null,
							]
								.filter(Boolean)
								.join(' ')}
							onDragEnter={(event) => event.preventDefault()}
							onDragOver={handleAppendDragOver}
							onDrop={commitDrop}
						/>
					) : null}
				</div>
			)}
		</div>
	);
};

DashboardLayoutGrid.propTypes = {
	layout: PropTypes.object.isRequired,
	userInfo: PropTypes.object,
	isEditing: PropTypes.bool,
	isResetting: PropTypes.bool,
	actionMessage: PropTypes.string,
	onFinishEditing: PropTypes.func.isRequired,
	onReorder: PropTypes.func.isRequired,
	onToggleHidden: PropTypes.func.isRequired,
	onRestoreDefault: PropTypes.func.isRequired,
};

export default memo(DashboardLayoutGrid);
